/**
 * LearningGate — decide CUÁNDO se abren los capítulos de aprendizaje y el
 * formulario de reportes (Envío AP-1).
 *
 *  · Al iniciar: una vez por sesión, después del telón de LMX (o 1,2 s si
 *    LMX no está), salvo que el usuario haya marcado «No volver a mostrar»
 *    o esté en medio de algo. «Saltear por ahora» = vuelve en la próxima
 *    sesión.
 *  · A pedido: evento `sml:learning-open` (menú «?» y último paso del
 *    recorrido) abre la biblioteca; `sml:feedback-open` abre el formulario.
 *  · Al cerrar avisa con `sml:learning-closed`, así el recorrido de LMX
 *    puede arrancar después (nunca se pisan).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { learningService } from '../../services/learningService';
import { useLmxStore } from '../../lmx/lmxStore';
import { useWorkflowStore } from '../../store/workflowStore';
import { LearningCenter } from './LearningCenter';
import { FeedbackModal } from './FeedbackModal';
import {
  EVT_CLOSED, EVT_DATA, EVT_FEEDBACK, EVT_OPEN, SESSION_KEY, shouldShowOnboarding,
} from './learningModel';

const NO_LMX_DELAY_MS = 1200;

const sessionShown = () => {
  try { return sessionStorage.getItem(SESSION_KEY) === '1'; } catch { return false; }
};
const markSession = () => {
  try { sessionStorage.setItem(SESSION_KEY, '1'); } catch { /* privado */ }
};

const busyNow = () => {
  const wf = useWorkflowStore.getState();
  if (wf.isRunningWorkflow) return true;
  if (Object.keys(wf.inFlight || {}).length > 0) return true;
  return !!document.querySelector('[role="dialog"]');
};

export const LearningGate = ({ lmxAvailable = false }) => {
  const lmxMode = useLmxStore((s) => s.mode);
  const [data, setData] = useState(null);
  const [view, setView] = useState(null);        // null | { mode, chapterId }
  const [feedback, setFeedback] = useState(null); // null | 'problem' | 'idea'
  const triedRef = useRef(false);

  const publish = (next) => {
    setData(next);
    window.dispatchEvent(new CustomEvent(EVT_DATA, { detail: next }));
  };

  const load = useCallback(async () => {
    try {
      const next = await learningService.get();
      publish(next);
      return next;
    } catch {
      return null; // sin tabla o sin red: la sección simplemente no aparece
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Apertura automática al iniciar ──
  useEffect(() => {
    if (triedRef.current || !data) return undefined;
    if (lmxAvailable && lmxMode === 'curtain') return undefined; // todavía en el telón
    if (!shouldShowOnboarding(data, { sessionShown: sessionShown() })) {
      triedRef.current = true;
      return undefined;
    }
    const t = setTimeout(() => {
      if (busyNow()) return; // se reintenta cuando cambie el modo de LMX
      triedRef.current = true;
      markSession();
      setView({ mode: 'onboarding' });
    }, lmxAvailable ? 350 : NO_LMX_DELAY_MS);
    return () => clearTimeout(t);
  }, [data, lmxAvailable, lmxMode]);

  // ── Apertura a pedido ──
  useEffect(() => {
    const onOpen = (e) => {
      setFeedback(null);
      setView({ mode: 'library', chapterId: e.detail?.chapterId });
      if (!data) load();
    };
    const onFeedback = (e) => {
      setView(null);
      setFeedback(e.detail?.kind === 'idea' ? 'idea' : 'problem');
    };
    window.addEventListener(EVT_OPEN, onOpen);
    window.addEventListener(EVT_FEEDBACK, onFeedback);
    return () => {
      window.removeEventListener(EVT_OPEN, onOpen);
      window.removeEventListener(EVT_FEEDBACK, onFeedback);
    };
  }, [data, load]);

  const close = (reason) => {
    const mode = view?.mode;
    setView(null);
    window.dispatchEvent(new CustomEvent(EVT_CLOSED, { detail: { mode, reason } }));
  };

  const onSeen = async (chapterId) => {
    if (!chapterId || data?.state?.seen?.includes(chapterId)) return;
    publish({ ...data, state: { ...data.state, seen: [...(data.state?.seen || []), chapterId] } });
    try { await learningService.saveState({ seen_chapter_id: chapterId }); } catch { /* se reintenta la próxima vez */ }
  };

  const onHideChange = async (hidden) => {
    publish({ ...data, state: { ...data.state, hidden }, onboarding: { ...data.onboarding, hidden } });
    try { await learningService.saveState({ hidden }); } catch { /* queda para la próxima */ }
  };

  return (
    <>
      <LearningCenter
        open={!!view && !!data}
        mode={view?.mode}
        initialChapterId={view?.chapterId}
        data={data}
        onClose={close}
        onSeen={onSeen}
        onHideChange={onHideChange}
      />
      <FeedbackModal open={!!feedback} initialKind={feedback || 'problem'}
                     onClose={() => setFeedback(null)} />
    </>
  );
};

export default LearningGate;
